import { useState } from 'react';
import { TileMark } from './tileMarks';
import { getCategoryAsset } from './categoryAssets';

interface CategoryLogoProps {
  slug: string;
  name: string;
  /** Admin-uploaded logo; wins over the bundled artwork when present. */
  src?: string | null;
  className?: string;
  /** Inner padding around the artwork, e.g. `p-1.5`. */
  padding?: string;
  loading?: 'lazy' | 'eager';
}

/**
 * Compares a `CatalogApp.logo` value against a bundled path, ignoring origin
 * and query string, so `https://cdn.example/categories/steam.svg` and
 * `/categories/steam.svg` are recognised as the same artwork.
 */
function isSameAsset(a: string, b: string): boolean {
  const path = (v: string) => {
    try {
      return new URL(v, 'https://placeholder.invalid').pathname;
    } catch {
      return v;
    }
  };
  return path(a) === path(b);
}

/**
 * Renders a category's artwork with a single source of truth for precedence,
 * shared by the hero rail and the category grid so they cannot drift:
 *
 *   1. `src`      — an admin upload from `/admin/catalog/apps/:id/logo`
 *   2. bundled    — the publisher SVG in `public/categories/`
 *   3. `TileMark` — the drawn placeholder, for slugs we ship no art for, or
 *                   when both of the above fail to load
 *
 * `TileMark` replaces the `<img>` entirely rather than sitting underneath it, so
 * a real logo is never seen through a pastel placeholder square.
 *
 * ## Why bundled artwork sits on a light plate
 *
 * The tile used to be tinted by the active theme and single-colour artwork was
 * colour-inverted to compensate. That broke several marks whose ink isn't
 * uniform, most visibly the games: `brawl-stars` is white+yellow+red on
 * transparent, `clash-of-clans` and `playstation` open with `fill="#ffffff"`.
 * Inverting a two-tone logo recolours its accents, and doing nothing at all made
 * the white parts vanish on the pale light/winter surface — which is why the
 * game icons appeared to disappear.
 *
 * Bundled marks now render on a fixed near-white plate (what playerok does, and
 * what publisher logos are designed for), so a logo looks identical in every
 * theme and needs no inversion. The plate is dropped for admin uploads, whose
 * ink we can't know. Note this compares *paths*, not `!src` — the seed writes
 * `/categories/<slug>.svg` into `CatalogApp.logo`, so a truthy `src` does not
 * mean "admin upload".
 */
export function CategoryLogo({
  slug,
  name,
  src,
  className = '',
  padding = 'p-1.5',
  loading = 'lazy',
}: CategoryLogoProps) {
  const bundled = getCategoryAsset(slug);
  const resolved = src ?? bundled?.src;
  const [broken, setBroken] = useState(false);

  if (!resolved || broken) {
    return <TileMark slug={slug} className={`size-full object-cover ${className}`} />;
  }

  const servedAsBundled = Boolean(bundled) && (!src || isSameAsset(src, bundled!.src));

  return (
    <div
      className={`absolute inset-0 ${servedAsBundled ? 'bg-[#f7f9fc]' : ''} ${className}`}
    >
      <img
        src={resolved}
        alt={name}
        loading={loading}
        className={`absolute inset-0 size-full object-contain ${padding}`}
        onError={(e) => {
          // A broken upload falls back to the bundled mark; a broken bundled mark
          // falls back to TileMark. Comparing paths (not strings) stops a failed
          // load from re-assigning the same URL and re-firing this handler.
          const img = e.currentTarget;
          if (bundled && !isSameAsset(resolved, bundled.src)) {
            img.src = bundled.src;
            return;
          }
          setBroken(true);
        }}
      />
    </div>
  );
}